import { Component, OnInit } from '@angular/core';
import { CashJournalService } from '../service/cash-journal.service';
import { Router } from '@angular/router';
import { NotificationService } from '../service/notification.service';

@Component({
    selector: 'app-cash-reconciliation',
    templateUrl: './cash-reconciliation.component.html',
    styleUrls: ['./cash-reconciliation.component.css']
})
export class CashReconciliationComponent implements OnInit {

    report: any = null;
    isLoading: boolean = false;
    selectedDate: string = new Date().toISOString().split('T')[0];
    confirmingIds: Set<number> = new Set();
    confirmedIds: Set<number> = new Set();
    Math = Math; // Expose Math for template

    constructor(
        private cashJournalService: CashJournalService,
        private router: Router,
        private notificationService: NotificationService
    ) { }

    ngOnInit(): void {
        this.loadReport();
    }

    loadReport() {
        this.isLoading = true;
        this.cashJournalService.getReconciliationReport(this.selectedDate, 'dashboard').subscribe({
            next: (data) => {
                this.report = data;
                if (this.report?.suggestions) {
                    this.report.suggestions = this.sortSuggestions(this.report.suggestions);
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error loading reconciliation report', err);
                this.isLoading = false;
                // Initialize default empty report on error so cards show up
                this.report = {
                    totalF8Entries: 0,
                    totalVirtualOrders: 0,
                    totalF8Amount: 0,
                    totalOrdersAmount: 0,
                    globalDiscrepancy: 0,
                    status: 'NO_DATA',
                    summary: 'Impossible de charger les données ou aucune donnée disponible.',
                    suggestions: [],
                    orphanOrders: []
                };
                this.notificationService.error('Erreur de connexion. Affichage par défaut.');
            }
        });
    }

    confirmMatch(suggestion: any) {
        // Multi-F8 IDs support
        const ids = suggestion.suggestedCashJournalIds || (suggestion.cashJournalId ? [suggestion.cashJournalId] : []);

        if (ids.length === 0 || !suggestion.suggestedOrderIds || suggestion.suggestedOrderIds.length === 0) {
            return;
        }

        // Add all IDs to confirming set
        ids.forEach((id: number) => this.confirmingIds.add(id));

        this.cashJournalService.confirmMatch(ids.length === 1 ? ids[0] : ids, suggestion.suggestedOrderIds).subscribe({
            next: (response) => {
                this.notificationService.success('Match confirmé avec succès !');
                ids.forEach((id: number) => {
                    this.confirmingIds.delete(id);
                    this.confirmedIds.add(id);
                });
                this.loadReport();
            },
            error: (err) => {
                console.error('Error confirming match', err);
                this.notificationService.error('Erreur lors de la confirmation du match');
                ids.forEach((id: number) => this.confirmingIds.delete(id));
            }
        });
    }

    goToOrderDetails(orderId: number) {
        this.router.navigate(['/orderDetails'], { queryParams: { id: orderId } });
    }

    /**
     * Sort suggestions for DISPLAY ONLY.
     * Priority: status rank (MATCHED first) → absolute écart asc (0 first) → confidence desc.
     * AVOIR suggestions (negative F8) with zero net écart rank higher than partial matches.
     * ⚠️ Does NOT touch any auto-reconciliation or business logic.
     */
    private sortSuggestions(suggestions: any[]): any[] {
        const statusRank: Record<string, number> = {
            'MATCHED':      0,
            'EXACT':        1,
            'PARTIAL':      2,
            'PROBABLE':     3,
            'AMBIGUOUS':    4,
            'UNMATCHED':    5,
            'ORPHAN_F8':    6,
            'ORPHAN_ORDER': 7,
        };

        return [...suggestions].sort((a, b) => {
            // 1. Status priority
            const rankA = statusRank[a.matchStatus] ?? 99;
            const rankB = statusRank[b.matchStatus] ?? 99;
            if (rankA !== rankB) return rankA - rankB;

            // 2. Absolute écart ascending (0 is perfect — includes AVOIR pairs that net to 0)
            const ecartA = Math.abs(a.discrepancy ?? 9999);
            const ecartB = Math.abs(b.discrepancy ?? 9999);
            if (Math.abs(ecartA - ecartB) > 0.01) return ecartA - ecartB;

            // 3. Confidence score descending
            const scoreA = a.confidenceScore ?? 0;
            const scoreB = b.confidenceScore ?? 0;
            return scoreB - scoreA;
        });
    }

    getMatchStatusClass(status: string): string {
        switch (status) {
            case 'EXACT': return 'bg-green-100 text-green-700 border-green-200';
            case 'MATCHED': return 'bg-emerald-100 text-emerald-700 border-emerald-200 font-bold';
            case 'PARTIAL': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'AMBIGUOUS': return 'bg-yellow-100 text-yellow-800 border-yellow-200 font-medium border-2 border-yellow-400';
            case 'PROBABLE': return 'bg-orange-100 text-orange-800 border-orange-200 font-medium';
            case 'UNMATCHED': return 'bg-red-100 text-red-700 border-red-200';
            case 'ORPHAN_F8': return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'ORPHAN_ORDER': return 'bg-purple-100 text-purple-700 border-purple-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    }

    getReportStatusClass(status: string): string {
        switch (status) {
            case 'FULLY_RECONCILED': return 'bg-green-500';
            case 'PARTIAL_RECONCILED': return 'bg-amber-500';
            case 'NOT_RECONCILED': return 'bg-red-500';
            default: return 'bg-gray-500';
        }
    }
}
